// components/patient/SmartTextArea.tsx
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { 
  ExclamationCircleIcon, 
  LightBulbIcon, 
  CheckCircleIcon,
  InformationCircleIcon 
} from '@heroicons/react/24/outline';

interface SmartTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  minLength?: number;
  maxLength?: number;
  showCharacterCount?: boolean;
  aiSuggestions?: boolean;
  feedbackType?: string;
  disabled?: boolean;
  rows?: number;
  className?: string;
}

// AI suggestion patterns based on feedback type
const suggestionPatterns = {
  medical_care: [
    "Consider mentioning when this occurred",
    "Include any symptoms or concerns",
    "Mention which staff members were involved",
    "Describe what you expected vs. what happened"
  ],
  nursing_care: [
    "Describe the specific interaction",
    "Mention the nurse's name if you remember",
    "Include the time and location",
    "Explain how this affected your care"
  ],
  facility_services: [
    "Specify your room number",
    "Describe the exact issue",
    "Mention when you first noticed it",
    "Include any safety concerns"
  ],
  food_services: [
    "Describe the meal and when it was served",
    "Mention any dietary restrictions",
    "Include temperature and quality details",
    "Suggest what would improve it"
  ],
  billing_insurance: [
    "Include relevant dates",
    "Mention specific charges if known",
    "Describe what information you need",
    "Reference any previous conversations"
  ],
  staff_behavior: [
    "Describe the specific behavior",
    "Include when and where it occurred",
    "Mention how it made you feel",
    "Suggest what would have been better"
  ],
  safety_security: [
    "Describe the safety concern in detail",
    "Include exact location and time",
    "Mention any immediate risks",
    "Suggest what actions are needed"
  ]
};

const SmartTextArea = forwardRef<HTMLTextAreaElement, SmartTextAreaProps>(({
  value,
  onChange,
  placeholder = "Enter your feedback here...",
  error,
  minLength = 0,
  maxLength = 2000,
  showCharacterCount = true,
  aiSuggestions = true,
  feedbackType,
  disabled = false,
  rows = 6,
  className = "",
}, ref) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<{
    sentiment: 'positive' | 'neutral' | 'negative';
    urgency: 'low' | 'medium' | 'high';
    completeness: number;
    suggestions: string[];
  } | null>(null);
  
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionTimeoutRef = useRef<NodeJS.Timeout>();

  useImperativeHandle(ref, () => textAreaRef.current!);

  useEffect(() => {
    if (aiSuggestions && feedbackType && suggestionPatterns[feedbackType as keyof typeof suggestionPatterns]) {
      setSuggestions(suggestionPatterns[feedbackType as keyof typeof suggestionPatterns]);
    }
  }, [feedbackType, aiSuggestions]);

  useEffect(() => {
    // Debounced AI analysis of content
    if (aiSuggestions && value.length > 20) {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
      
      suggestionTimeoutRef.current = setTimeout(() => {
        analyzeContent(value);
      }, 1000);
    }

    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, [value, aiSuggestions]);

  const analyzeContent = (content: string) => {
    // Simple AI-like analysis (in production, this would call actual AI service)
    const words = content.toLowerCase();
    
    // Sentiment analysis
    const positiveWords = ['good', 'great', 'excellent', 'wonderful', 'thank', 'appreciate', 'helpful'];
    const negativeWords = ['bad', 'terrible', 'awful', 'horrible', 'pain', 'problem', 'issue', 'concern', 'upset'];
    
    const positiveScore = positiveWords.filter(word => words.includes(word)).length;
    const negativeScore = negativeWords.filter(word => words.includes(word)).length;
    
    let sentiment: 'positive' | 'neutral' | 'negative';
    if (positiveScore > negativeScore) sentiment = 'positive';
    else if (negativeScore > positiveScore) sentiment = 'negative';
    else sentiment = 'neutral';

    // Urgency analysis
    const urgentWords = ['emergency', 'urgent', 'immediate', 'critical', 'serious', 'danger', 'pain'];
    const urgentScore = urgentWords.filter(word => words.includes(word)).length;
    
    let urgency: 'low' | 'medium' | 'high';
    if (urgentScore >= 2) urgency = 'high';
    else if (urgentScore >= 1) urgency = 'medium';
    else urgency = 'low';

    // Completeness analysis
    const hasWho = /\b(nurse|doctor|staff|person|employee)\b/i.test(content);
    const hasWhen = /\b(today|yesterday|morning|afternoon|evening|night|\d+:\d+|am|pm)\b/i.test(content);
    const hasWhere = /\b(room|floor|department|unit|\d+[a-z]?)\b/i.test(content);
    const hasWhat = content.length > 50;
    
    const completeness = [hasWho, hasWhen, hasWhere, hasWhat].filter(Boolean).length * 25;

    // Generate contextual suggestions
    const contextSuggestions: string[] = [];
    if (!hasWho) contextSuggestions.push("Consider mentioning which staff member was involved");
    if (!hasWhen) contextSuggestions.push("Adding when this occurred would be helpful");
    if (!hasWhere) contextSuggestions.push("Include your location (room number, department)");
    if (content.length < 100) contextSuggestions.push("More details would help us understand better");

    setAiAnalysis({
      sentiment,
      urgency,
      completeness,
      suggestions: contextSuggestions
    });
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  const insertSuggestion = (suggestion: string) => {
    const currentValue = value;
    const newValue = currentValue ? `${currentValue}\n\n${suggestion}` : suggestion;
    onChange(newValue);
    setShowSuggestions(false);
    
    // Focus back on textarea
    if (textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(newValue.length, newValue.length);
    }
  };

  const getCharacterCountColor = () => {
    const length = value.length;
    const percentage = (length / maxLength) * 100;
    
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-gray-800';
  };

  const getSentimentColor = () => {
    if (!aiAnalysis) return 'text-gray-800';
    switch (aiAnalysis.sentiment) {
      case 'positive': return 'text-green-600';
      case 'negative': return 'text-red-600';
      default: return 'text-gray-900';
    }
  };

  const getUrgencyColor = () => {
    if (!aiAnalysis) return 'text-gray-800';
    switch (aiAnalysis.urgency) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Text area */}
      <div className="relative">
        <textarea
          ref={textAreaRef}
          value={value}
          onChange={handleTextAreaChange}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={`
            block w-full rounded-lg border-gray-300 shadow-sm resize-none
            text-sm font-normal text-gray-900 placeholder:text-gray-400
            bg-white transition-all duration-200
            hover:border-gray-400 hover:shadow-md
            focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:shadow-md
            disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-gray-200
            ${error ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' : ''}
            ${aiAnalysis?.urgency === 'high' ? 'border-red-300 hover:border-red-400' : ''}
          `}
        />
        
        {/* Character count */}
        {showCharacterCount && (
          <div className="absolute bottom-3 right-3 text-sm pointer-events-none">
            <span className={getCharacterCountColor()}>
              {value.length}{maxLength > 0 && `/${maxLength}`}
            </span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center text-sm text-red-600">
          <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* AI analysis indicators */}
      {aiAnalysis && value.length > 20 && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <span className="text-gray-900 mr-2">Tone:</span>
              <span className={`font-medium ${getSentimentColor()}`}>
                {aiAnalysis.sentiment.charAt(0).toUpperCase() + aiAnalysis.sentiment.slice(1)}
              </span>
            </div>
            
            <div className="flex items-center">
              <span className="text-gray-900 mr-2">Priority:</span>
              <span className={`font-medium ${getUrgencyColor()}`}>
                {aiAnalysis.urgency.charAt(0).toUpperCase() + aiAnalysis.urgency.slice(1)}
              </span>
            </div>
            
            <div className="flex items-center">
              <span className="text-gray-900 mr-2">Details:</span>
              <div className="w-20 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${aiAnalysis.completeness}%` }}
                ></div>
              </div>
              <span className="text-gray-900 ml-2 text-xs">{aiAnalysis.completeness}%</span>
            </div>
          </div>
        </div>
      )}

      {/* AI suggestions */}
      {aiSuggestions && (suggestions.length > 0 || (aiAnalysis && aiAnalysis.suggestions.length > 0)) && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700"
          >
            <LightBulbIcon className="h-4 w-4 mr-1" />
            {showSuggestions ? 'Hide' : 'Show'} writing suggestions
          </button>

          {showSuggestions && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
                <InformationCircleIcon className="h-4 w-4 mr-1" />
                Suggestions to improve your feedback:
              </h4>
              
              <div className="space-y-2">
                {/* Content-based AI suggestions */}
                {aiAnalysis && aiAnalysis.suggestions.map((suggestion, index) => (
                  <button
                    key={`ai-${index}`}
                    type="button"
                    onClick={() => insertSuggestion(`[Consider: ${suggestion}]`)}
                    className="block w-full text-left p-2 text-sm text-blue-800 hover:bg-blue-100 rounded transition-colors"
                  >
                    • {suggestion}
                  </button>
                ))}
                
                {/* Category-based suggestions */}
                {suggestions.slice(0, 3).map((suggestion, index) => (
                  <div
                    key={`cat-${index}`}
                    className="text-sm text-blue-800 p-2 bg-blue-100 rounded"
                  >
                    💡 {suggestion}
                  </div>
                ))}
              </div>
              
              {aiAnalysis?.urgency === 'high' && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    This appears to be urgent. Consider calling for immediate assistance if needed.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Length validation */}
      {minLength > 0 && value.length > 0 && value.length < minLength && (
        <div className="flex items-center text-sm text-yellow-600">
          <InformationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          Please provide at least {minLength - value.length} more characters for a complete description
        </div>
      )}

      {/* Success indicator */}
      {minLength > 0 && value.length >= minLength && !error && (
        <div className="flex items-center text-sm text-green-600">
          <CheckCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          Thank you for providing detailed feedback
        </div>
      )}
    </div>
  );
});

SmartTextArea.displayName = 'SmartTextArea';

export default SmartTextArea;